import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { Mail, Phone, CheckCircle, XCircle, Download, ChevronUp, ChevronDown } from "lucide-react"
import TechExperienceCard from "./tech-experience-card"
import DecisionSummaryCard from "./decision-summary-card"
import WorkExperienceCard from "./work-experience-card"
import { CandidateSummary } from "openapi/api/model"
import { TechExperienceDetail } from '../../../../openapi/api/model/techExperienceDetail';
import { Button } from '@/components/ui/button';
import { useState } from "react"
import { TaggedExperience } from "./types"

type CandidateProfileProps = {
  candidate: CandidateSummary,
  onDownload:()=>void
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
}

export default function CandidateProfile({ candidate,onDownload }: CandidateProfileProps) {
  const { case: candidateCase } = candidate
  const [showAllExperiences, setShowAllExperiences] = useState(false)

    // Limit work experiences to 3 if not showing all
const displayedExperiences = candidateCase?.taggedExperiences
  ? showAllExperiences
    ? candidateCase.taggedExperiences
    : candidateCase.taggedExperiences.slice(0, 3)
  : []


  return (
    <div className="container mx-auto p-6 space-y-8">
      <Card className="mx-2 bg-white">
        <CardHeader>
        <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-2xl font-bold">
                {candidate.firstName} {candidate.middleName} {candidate.lastName}
              </CardTitle>
              <div className="flex items-center space-x-4 text-muted-foreground mt-2">
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>{candidate.email}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <span>{candidate.phoneNumber}</span>
                </div>
              </div>
            </div>
            <Button onClick={onDownload} variant="outline">
              <Download className="mr-2 h-4 w-4" />
              Download PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <h3 className="font-semibold">Location</h3>
              <p>
                {candidate.city}, {candidate.state}, {candidate.country}
              </p>
            </div>
            <div>
              <h3 className="font-semibold">Position Applied For</h3>
              <p>{candidate.positionAppliedFor}</p>
            </div>
            <div>
              <h3 className="font-semibold">Desired Salary</h3>
              <p>{candidate.desiredSalary}</p>
            </div>
            <div>
              <h3 className="font-semibold">Available Start Date</h3>
              <p>{formatDate(candidate.availableStartDate)}</p>
            </div>
            <div>
              <h3 className="font-semibold">Employment Type</h3>
              <p>{candidate.employmentType}</p>
            </div>
            <div>
              <h3 className="font-semibold">Education</h3>
              <p>
                {candidate.educationLevel} - {candidate.universityName}
              </p>
            </div>
            <div>
              <h3 className="font-semibold">Pre-Qualified</h3>
              <div className="flex items-center space-x-2">
                {candidate.isQualified ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <span>{candidate.isQualified ? "Yes" : "No"}</span>
              </div>
            </div>
          </div>
          <Separator className="my-6" />
          <div>
            <h3 className="font-semibold text-lg mb-3">Work Authorization</h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <h4 className="font-medium">Requires Sponsorship</h4>
                <p>{candidate.workAuthorization?.requiresSponsorship ? "Yes" : "No"}</p>
              </div>
              <div>
                <h4 className="font-medium">Work Authorization</h4>
                <p>{candidate.workAuthorization?.acceptedStatuses}</p>
              </div>
              <div>
                <h4 className="font-medium">Security Clearance</h4>
                <p>{candidate.workAuthorization?.hasSecurityClearance ? "Yes" : "No"}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mx-2 bg-white">
        <CardHeader>
          <CardTitle>Matched Skills</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {candidate.matchedSkills?.map((skill, index) => (
              <div key={index} className="flex items-center space-x-2 bg-muted rounded-full px-3 py-1">
                {skill.exists ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-500" />
                )}
                <span>{skill.skill}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      {
        candidateCase &&(
          <>
          <Card className="mx-2 bg-white">
          <CardHeader>
            <CardTitle>Work Experience</CardTitle>
          </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {displayedExperiences.map((exp, index) => (
              <WorkExperienceCard key={index} experience={exp as TaggedExperience} />
            ))}
          </div>

          {candidateCase.taggedExperiences.length > 3 && (
            <div className="flex justify-center mt-6">
              <Button
                variant="outline"
                onClick={() => setShowAllExperiences(!showAllExperiences)}
                className="flex items-center gap-1"
              >
                {showAllExperiences ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    Show {candidateCase.taggedExperiences.length - 3} more experiences
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
        </Card>
  
        <Card className="mx-2 bg-white">
          <CardHeader>
            <CardTitle>Technical Experience</CardTitle>
          </CardHeader>
          <CardContent>
            <TechExperienceCard techExperience={candidateCase.decisionMakingSummary?.tech_experience_details as TechExperienceDetail[]} />
          </CardContent>
        </Card>

          </>

        )
      }  
        <Card className="mx-2 bg-white">
          <CardHeader>
            <CardTitle>Decision Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <DecisionSummaryCard
              summary={candidateCase?.decisionMakingSummary}
              decision={candidateCase?candidateCase.decision:(!candidate.isQualified?'Failed Pre-qualification':candidate._id)}
              reasoning={!candidate.isQualified? candidate.reasoning: ''}
              candidate={candidate}
            />
          </CardContent>
        </Card>          

    </div>
  )
}
